/* eslint-disable react/jsx-max-depth */
import { SearchActionFilter } from '@enview/interface/types/Bill';
import { BillSearchCriteria, SavedSearch } from '@enview/interface/types/BillSearch';
import { ChamberType } from '@enview/interface/types/LegislativeSession';
import { OrganizationUser } from '@enview/interface/types/OrganizationUser';
import { TeamMembership } from '@enview/interface/types/Team';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import isEqual from 'lodash-es/isEqual';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { DecodedValueMap, QueryParamConfigMap } from 'use-query-params';
import * as BillAnalytics from '../../../analytics/BillAnalytics';
import { BillAPI, JurisdictionAPI, TagAPI } from '../../../api';
import { CURRENT_SESSION_FILTER } from '../../../api/jurisdiction';
import FilterIcon from '../../../components/svg/FilterIcon';
import { g as abilityGlossary } from '../../../config/ability';
import {
  checkPermission,
  getJurisdictionsForTeamMode,
  getTeamMode,
  openEditSearchModal,
  openSavedSearchModal,
  updateOrganizationUser,
} from '../../../dux';
import { Thunk } from '../../../dux/@types';
import featureFlags, { FrontendFeature } from '../../../featureFlags';
import {
  defaultLegislativeTypeSelection,
  defaultStatusSelection,
  getInsightsPolicyTopicOptions,
  getSessionOptions,
  getStatusOptionsForJurisdictions,
} from '../../../helpers/BillSearchHelper';
import { SortByParam, SortOrder } from '../../../models/SortOrder';
import { checkForInsightsPolicyTopicEligibility } from '../../Shared/BillInsights/utils';
import DropDownButton from '../../Shared/Buttons/DropDownButton';
import CustomDropdown from '../../Shared/DropDowns/CustomDropdown';
import '../../Shared/Search/Search.scss';
import JurisdictionDropdown from '../../Shared/Selectors/JurisdictionDropdown';
import GateTooltip, { GateTooltipFeature } from '../../Shared/Tooltips/TooltipGate';
import AdvSearchFilters from './AdvSearchFilters';
import KeywordSearch from './KeywordSearch';
import { OptionType } from '../../Shared/DropDowns/MultipleSelectDropDown';
import MultiSelectForm from '../../Shared/Forms/MultiSelectForm';
import EndOfSessionReportHideable from '../EndOfSessionReportHideable';

type SearchFiltersProps = {
  organizationUser?: OrganizationUser;
  query: DecodedValueMap<QueryParamConfigMap>;
  criteria: BillSearchCriteria;
  resetScroll: () => void;
  setQuery: (arg: any) => void;
  setSearching: (arg: boolean) => void;
  savedSearch?: SavedSearch;
};

const defaultProps = { savedSearch: null };

const SearchFilters = (props: SearchFiltersProps): ReactElement => {
  const dispatch = useDispatch();

  const {
    organizationUser,
    resetScroll,
    criteria,
    query,
    setQuery,
    setSearching,
    savedSearch,
  } = props;

  const teamModeId = useSelector(getTeamMode);
  const { t } = useTranslation();

  const activeTeamMembership = organizationUser?.teamMemberships.find(
    (tm: TeamMembership) => tm.teamId === teamModeId,
  );
  const isUserAdminOrCreator =
    activeTeamMembership &&
    ['admin', 'creator'].includes(
      // Workspace admins can edit all saved searches in the workspace
      activeTeamMembership.role,
    );

  const canSaveSearches = (): Thunk<boolean> => {
    return dispatch(
      checkPermission(abilityGlossary.EDIT, abilityGlossary.SAVED_SEARCHES, true),
    );
  };

  const canViewInsightTopicsFilter = () => {
    return dispatch(
      checkPermission(
        abilityGlossary.VIEW,
        abilityGlossary.BILL_SEARCH_INSIGHT_TOPICS_FILTER,
        true,
      ),
    );
  };

  const saveSearchFeatureAvailable = canSaveSearches() && teamModeId && teamModeId > -1;

  // Determine whether the user has "save" permissions to show/hide the save button
  const userCanSaveSearch =
    !savedSearch || // All users can save new searches
    isUserAdminOrCreator ||
    // Creator of the saved search can always edit the search
    savedSearch.creatorUserId === organizationUser?.id;

  const orgJurisdictions = useSelector(getJurisdictionsForTeamMode);
  const [searchChanged, setSearchChanged] = useState(false);
  const [showAdvSearch, setShowAdvSearch] = useState(false);

  const [jurisdictions, setJurisdictions] = useState(query.jurisdictions);
  const [keywords, setKeywords] = useState<string>(query.keywords);
  const defaultOrder = keywords
    ? SortOrder.getBillSortOrderFromParam(SortByParam.RELEVANCE)
    : SortOrder.getBillSortOrderFromParam(SortByParam.LATEST);
  const [sessions, setSessions] = useState(
    query.sessions.filter((s: string | null) => s !== null),
  );
  const [chamber, setChamber] = useState(query.chamber);
  const [includeTrackedBills, setIncludeTrackedBills] = useState<boolean>(
    query.includeTrackedBills,
  );
  const [onlyShowMomentumBills, setOnlyShowMomentumBills] = useState<boolean>(
    query.onlyShowMomentumBills,
  );
  const [insightsPolicyTopic, setInsightsPolicyTopic] = useState(
    query.insightsPolicyTopic,
  );
  const [status, setStatus] = useState(
    query.status.filter((s: string | null) => s !== null),
  );

  const [legislativeType, setLegislativeType] = useState(
    query.legislativeType.filter((s: string | null) => s !== null),
  );

  const { data: filteredTags } = TagAPI.endpoints.getTags.useQuery(undefined);

  const [selectedTags, setSelectedTags] = useState<string[] | undefined>(query.tags);
  const [selectedTagFilterOption, setSelectedTagFilterOption] = useState<
    string | undefined
  >(query.tagFilterMode);

  const tagOptions = useMemo((): OptionType<number>[] | undefined => {
    return filteredTags?.map((tag) => {
      return {
        label: tag.name,
        value: tag.id,
      };
    });
  }, [filteredTags]);

  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  const [billAction, setBillAction] = useState<SearchActionFilter | undefined>(
    query.searchActionType
      ? {
          type: query.searchActionType,
          fromDate: query.searchActionFromDate,
          toDate: query.searchActionToDate,
        }
      : undefined,
  );
  const [showTopicFilter, setShowTopicFilter] = useState(false);

  const selectedBillOrder = query.order
    ? SortOrder.getBillSortOrderFromParam(query.order)
    : defaultOrder;

  const { data: jurisdictionSessions } = JurisdictionAPI.endpoints.getSessions.useQuery(
    jurisdictions as string[],
  );

  const sessionOptions =
    jurisdictions.length > 0 && jurisdictionSessions
      ? getSessionOptions(jurisdictionSessions)
      : getSessionOptions([]);

  const checkViewEndOfSessionReportPermission = (): Thunk<boolean> => {
    return dispatch(
      checkPermission(
        abilityGlossary.VIEW,
        abilityGlossary.BILL_SUMMARY_END_OF_SESSION,
        true,
      ),
    );
  };
  const jurisdictionAbbreviationsWithEndOfSessionReports = !jurisdictionSessions
    ? []
    : jurisdictionSessions
        .filter((session) => session.reportTopicIds.length > 0)
        .map((session) => session.jurisdictionAbbreviation);
  const endOfSessionReportEnabled =
    featureFlags.isFeatureEnabledForUser(
      FrontendFeature.EndOfSessionReport,
      organizationUser,
    ) &&
    jurisdictions.length === 1 &&
    jurisdictionAbbreviationsWithEndOfSessionReports.includes(jurisdictions[0]) &&
    checkViewEndOfSessionReportPermission();

  const statusOptions = useMemo(
    () => getStatusOptionsForJurisdictions(jurisdictions),
    [jurisdictions],
  );

  const { data: insightsPolicyTopics } =
    BillAPI.endpoints.getInsightsPolicyTopics.useQuery(
      canViewInsightTopicsFilter() ? {} : skipToken,
    );

  const insightsPolicyTopicOptions = checkForInsightsPolicyTopicEligibility(
    jurisdictions,
  )
    ? getInsightsPolicyTopicOptions(insightsPolicyTopics)
    : getInsightsPolicyTopicOptions([]);

  // re-set all search filters depending on query change, namely after pressing browser back button
  useEffect(() => {
    setKeywords(query.keywords);
    setJurisdictions(query.jurisdictions);
    setSessions(query.sessions.filter((s: string | null) => s !== null));
    setChamber(query.chamber);
    setStatus(query.status.filter((s: string | null) => s !== null));
    setLegislativeType(query.legislativeType.filter((s: string | null) => s !== null));
    setBillAction({
      type: query.searchActionType,
      fromDate: query.searchActionFromDate,
      toDate: query.searchActionToDate,
    });
    setIncludeTrackedBills(query.includeTrackedBills);
    setOnlyShowMomentumBills(query.onlyShowMomentumBills);
    setInsightsPolicyTopic(query.insightsPolicyTopic);
    setSelectedTags(query.tags);
    setSelectedTagFilterOption(query.tagFilterMode);
  }, [query]);

  const updateSearchJurisdictions = (
    orgUser: OrganizationUser,
    newJurs: string[],
  ): void => {
    if (newJurs.length === 0 || !organizationUser) {
      return;
    }
    const prevJurisdicions = new Set(
      organizationUser.searchJurisdictions?.map((j) => j.abbreviation),
    );
    const newJurisdictions = new Set(newJurs);

    if (!isEqual(prevJurisdicions, newJurisdictions)) {
      dispatch(
        updateOrganizationUser(
          {
            firstName: orgUser.firstName,
            lastName: orgUser.lastName,
            email: orgUser.email,
            searchJurisdictions: orgJurisdictions.filter((j) =>
              newJurs.includes(j.abbreviation),
            ),
          },
          orgUser,
        ),
      );
    }
  };

  const saveOrEdit = (): void => {
    BillAnalytics.savedSearchQuery(
      jurisdictions as string[],
      sessions as string[],
      status as string[],
      includeTrackedBills,
      billAction,
      keywords,
      chamber as string,
      legislativeType as string[],
    );
    if (savedSearch) {
      dispatch(
        openEditSearchModal({ ...savedSearch, searchCriteria: criteria }, 'update'),
      );
    } else {
      dispatch(openSavedSearchModal(criteria));
    }
  };

  const search = (): void => {
    BillAnalytics.trackBillsSearched(
      jurisdictions as string[],
      sessions as string[],
      status,
      teamModeId,
      keywords,
      organizationUser,
      chamber,
      includeTrackedBills,
      onlyShowMomentumBills,
      legislativeType,
      billAction,
      insightsPolicyTopic,
      savedSearch,
    );

    if (organizationUser) {
      updateSearchJurisdictions(organizationUser, jurisdictions as string[]);
    }

    const order = (selectedBillOrder && selectedBillOrder.getQueryParam()) || undefined;
    setSearching(true);
    setQuery({
      page: 1,
      sessions,
      keywords,
      jurisdictions,
      order,
      status,
      chamber,
      includeTrackedBills,
      onlyShowMomentumBills,
      legislativeType,
      searchActionType: billAction?.type,
      searchActionFromDate: billAction?.fromDate,

      searchActionToDate: billAction?.toDate,
      insightsPolicyTopic,

      tags: selectedTags?.slice(),
      tagFilterMode: selectedTagFilterOption,
    });

    resetScroll();
    setSearchChanged(false);
  };

  const clearAll = (): void => {
    setJurisdictions([]);
    setKeywords('');
    setSessions([CURRENT_SESSION_FILTER]);
    setChamber(ChamberType.BOTH);
    setStatus(defaultStatusSelection);
    setLegislativeType(defaultLegislativeTypeSelection);
    setBillAction(undefined);
    setIncludeTrackedBills(true);
    setOnlyShowMomentumBills(false);
    setSearchChanged(true);
    setInsightsPolicyTopic(undefined);
    setSelectedTags([]);
    setSelectedTagFilterOption('any');
  };

  const renderMultiSelectOptions = (
    currentSelection: string[] | undefined,
    options: OptionType<string>[],
    updateSelection: (values: string[]) => void,
  ): ReactElement => {
    return (
      <MultiSelectForm
        currentSelection={currentSelection ?? []}
        options={options}
        showClearAll
        showSelectAll
        showSelectButtonsBottom
        singleColumn
        updateSelection={updateSelection}
      />
    );
  };

  const handleInsightsPolicyTopicFiltersChange = (
    updatedInsightsPolicyTopicFilters: (string | null)[],
  ): void => {
    if (isEqual(updatedInsightsPolicyTopicFilters, insightsPolicyTopic)) return;
    setInsightsPolicyTopic(updatedInsightsPolicyTopicFilters);
    setSearchChanged(true);
  };

  const dropdownTargetButton = (
    isDisplayed: boolean,
    label: string,
    onClick: () => void,
    selectedCount?: number,
    disabled?: boolean,
    customContent?: ReactElement,
    ineligibleJurisdictions?: boolean,
  ): ReactElement => {
    // For the purpose of this component, assuming that filter dropdowns will be disabled for
    // the reason of feature-gating logged-out users
    const id = `${label.replace(/\s/g, '-').toLowerCase()}-filter`;
    return (
      <>
        <DropDownButton
          disabled={ineligibleJurisdictions ?? disabled}
          dropdownDisplayed={isDisplayed}
          id={id}
          label={label}
          onClick={onClick}
          selectedCount={selectedCount}
        />
        {disabled && (
          <GateTooltip
            accountType="pro"
            anchorId={id}
            customContent={customContent}
            place="bottom"
          />
        )}
      </>
    );
  };
  /*
    Logged-out users get a dummy button with a tooltip, logged-in users
    get a working "Save" button unless they are viewing a search created
    by someone else (and are not admins)
  */
  const renderSavedSearchButton = (): ReactElement => {
    if (!saveSearchFeatureAvailable) {
      return (
        <>
          <button
            aria-disabled="true"
            className="btn btn-sm btn-light float-right disabled"
            id="search-save-button"
            type="button"
          >
            Save
          </button>
          <GateTooltip
            accountType="pro"
            anchorId="search-save-button"
            customContent={
              <Trans
                components={{
                  savedSearchLink: <a href={t('urls.savedSearches')} target="_blank" />,
                  gate: <p className="tooltip-header" />,
                }}
                i18nKey="featureGating.tooltipText.savedSearch"
                key="featureGating.tooltipText.savedSearch"
              />
            }
            featureName={GateTooltipFeature.SavedSearches}
            place="bottom"
          />
        </>
      );
    }
    if (userCanSaveSearch) {
      return (
        <button
          className="btn btn-sm btn-light float-right"
          disabled={searchChanged}
          id="search-save-button"
          onClick={saveOrEdit}
          type="button"
        >
          Save
        </button>
      );
    }
    return <></>;
  };

  const renderBillTopicsSelector = (): ReactElement => {
    return (
      <div className="adv-filter bill-action">
        <CustomDropdown
          setShowDropdown={setShowTopicFilter}
          showDropdown={showTopicFilter}
          target={dropdownTargetButton(
            showTopicFilter,
            t('buttons.search.billTopics'),
            () => setShowTopicFilter(!showTopicFilter),
            insightsPolicyTopic?.length,
            !canViewInsightTopicsFilter(),
            <Trans
              components={{
                billTopicLink: (
                  <a href={t('bill.sidebar.insightBillTopics.url')} target="_blank" />
                ),
                gate: <p className="tooltip-header" />,
              }}
              i18nKey="featureGating.tooltipText.billTopicDropdown"
              key="featureGating.tooltipText.billTopicDropdown"
            />,
            // disable dropdown if none of the selected jurisdictions has bill topics
            insightsPolicyTopicOptions.length === 0,
          )}
        >
          <div className="adv-search-selector">
            {renderMultiSelectOptions(
              insightsPolicyTopic,
              insightsPolicyTopicOptions,
              handleInsightsPolicyTopicFiltersChange,
            )}
          </div>
        </CustomDropdown>
      </div>
    );
  };

  return (
    <div className="bill-search container">
      <div className="row">
        <div className="col-lg bill-search-filters">
          <KeywordSearch
            hasSearchChanged={searchChanged}
            keywords={keywords}
            placeholder={t('bill.search.keywordPlaceholder')}
            search={search}
            setKeywords={setKeywords}
            setSearchChanged={setSearchChanged}
          />
          <div className="vert-line-break" />
          <JurisdictionDropdown
            jurisdictions={orgJurisdictions}
            jurisdictionsSelected={jurisdictions}
            setJurisdictionsSelected={setJurisdictions}
            setSearchChanged={setSearchChanged}
          />
        </div>
        <div className="d-inline-flex col-auto bill-search-buttons">
          <button
            className={`btn btn-sm btn-outline-secondary ${
              showAdvSearch ? 'active' : ''
            }`}
            id="adv-search-btn"
            onClick={() => setShowAdvSearch(!showAdvSearch)}
            type="button"
          >
            <FilterIcon color={showAdvSearch ? '#ffffff' : ''} />
          </button>
          <button
            className="btn btn-sm btn-outline-secondary float-right"
            disabled={!searchChanged}
            id="search-button"
            onClick={() => search()}
            type="button"
          >
            {t('buttons.search.submitButtonText')}
          </button>
          {renderSavedSearchButton()}
        </div>
      </div>
      {showAdvSearch && (
        <AdvSearchFilters
          chamber={chamber}
          clearAllFilters={clearAll}
          isIncludeTrackedBillsOn={includeTrackedBills}
          isOnlyShowMomentumBillsOn={onlyShowMomentumBills}
          legislativeType={legislativeType}
          selectedTagFilterOption={selectedTagFilterOption}
          selectedTags={selectedTags}
          sessionOptions={sessionOptions}
          sessions={sessions}
          setBillAction={setBillAction}
          setChamber={setChamber}
          setIncludeTrackedBills={setIncludeTrackedBills}
          setLegislativeType={setLegislativeType}
          setOnlyShowMomentumBills={setOnlyShowMomentumBills}
          setSearchChanged={setSearchChanged}
          setSelectedTagFilterOption={setSelectedTagFilterOption}
          setSelectedTags={setSelectedTags}
          setSessions={setSessions}
          setStatus={setStatus}
          status={status}
          statusOptions={statusOptions}
          tagOptions={tagOptions}
          billAction={billAction}
          // topic selector gets rendered in advanced search controls iff EOSR flag is off
          billTopicsSelector={renderBillTopicsSelector()}
        />
      )}
      {endOfSessionReportEnabled && jurisdictionSessions && (
        <EndOfSessionReportHideable
          allSessions={jurisdictionSessions}
          allTopics={insightsPolicyTopics}
          jurisdictionAbbreviation={jurisdictions[0]}
          sessionsSelected={sessions}
        />
      )}
    </div>
  );
};

SearchFilters.defaultProps = defaultProps;

export default SearchFilters;
